import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { combineLatest, of, timer } from 'rxjs';
import {
    finalize,
    first,
    map,
    shareReplay,
    switchMap,
    tap,
    timeoutWith
} from 'rxjs/operators';
import {
    ClientService,
    DynamicFormOptionsService
} from 'src/app/core/services';
import { ScrollTopService } from '../../../../core/services/scroll-top.service';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import {
    applyFlyoutUpdate,
    buildApplicationPageValueModel,
    buildDynamicForm,
    buildFormValuesFromAnswers,
    initFlyoutObserver,
    isSectionVisible,
    matchesConditions,
    normalizeValidatorConfigs,
    positionFlyoutTooltip
} from '../../../../core/helpers';
import { DynamicFormApiService } from '../../../../core/services/dynamic-form-api.service';
import {
    ClientPageSectionModel,
    PageSectionColumnModel,
    PageSectionFieldValidator,
    PageSectionRowModel
} from '../../../../domain/models/dynamic-form-schema.model';
import { FormPage } from '../../../../domain/models/form-page.enum';
import { ChangesNotSavedComponent } from '../../../../shared/components/changes-not-saved';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';
import { SectionFlyoutComponent } from '../../components/section-flyout/section-flyout.component';
import { Application, SelectedProvider } from '../../models';
import { applicationPresent } from '../../operators';
import { ApplicationService, CurrentApplicationService } from '../../services';

@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./../../application-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCardModule,
        MatFormFieldModule,
        DynamicFormComponent,
        FlexLayoutModule,
        MatDividerModule,
        SectionFlyoutComponent
    ]
})
export class PersonalInformationComponent
    extends ChangesNotSavedComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    /**
     * List to populate the Providers flyout
     */
    selectedProviders: SelectedProvider[] | undefined;
    submitted = false; // because we are extending ChangesNotSavedComponent
    submitting = false;
    activeFlyoutIndex = -1;
    flyoutFlipUp = false;
    private applicationId?: number;
    private loadedSections: ClientPageSectionModel[] = [];
    private flyoutObserver?: IntersectionObserver;

    @ViewChildren('sectionWrapper') sectionWrappers!: QueryList<ElementRef>;

    personalInformation: UntypedFormGroup;
    singleProviderName: string | undefined;
    hasSiteOptions = false;

    client$ = this._client.latest;

    formSections$ = this._currentApplication.latest.pipe(
        applicationPresent(),
        first(),
        switchMap((application: Application) => {
            if (application?.applicationId) {
                this.applicationId = application.applicationId;
                // Load provider site options and enrich form sections with applicationId
                return this.applicationService
                    .getProviderSiteOptions(application.applicationId)
                    .pipe(
                        switchMap((siteOptions) => {
                            this.singleProviderName = siteOptions.providerName;
                            this.hasSiteOptions =
                                (siteOptions.sites?.length ?? 0) > 0;
                            return this.dynamicFormApi.getFormSections(
                                FormPage.personalInformation
                            );
                        }),
                        switchMap((sections) => {
                            this.dynamicFormOptionsService.enrichSectionsWithOptions(
                                sections,
                                application.applicationId
                            );

                            // Collect all site-select options$ observables to wait for them
                            const siteSelectOptions$ = [];
                            for (const section of sections) {
                                for (const row of section.rows || []) {
                                    for (const field of row.fields || []) {
                                        if (
                                            field.type === 'site-select' &&
                                            field.options$
                                        ) {
                                            siteSelectOptions$.push(
                                                field.options$
                                            );
                                        }
                                    }
                                }
                            }

                            // Wait for all options to resolve before emitting sections
                            if (siteSelectOptions$.length === 0) {
                                return of(sections);
                            }

                            return combineLatest(siteSelectOptions$).pipe(
                                // Safety timeout: if site-select options don't resolve within 5s, proceed anyway
                                timeoutWith(
                                    5000,
                                    timer(0).pipe(
                                        tap(() => {
                                            console.warn(
                                                '[PersonalInfo] site-select options timeout - proceeding with empty options'
                                            );
                                        }),
                                        map(() => sections)
                                    )
                                ),
                                map(() => sections)
                            );
                        })
                    );
            } else {
                return this.dynamicFormApi
                    .getFormSections(FormPage.personalInformation)
                    .pipe(
                        switchMap((sections) => {
                            this.dynamicFormOptionsService.enrichSectionsWithOptions(
                                sections
                            );
                            return of(sections);
                        })
                    );
            }
        }),
        shareReplay(1)
    );

    formValue$ = this._currentApplication.latest.pipe(
        applicationPresent(),
        first(),
        switchMap((application: Application) => {
            if (application?.applicationId) {
                this.applicationId = application.applicationId;
                return this.applicationService.getDynamicFormValues(
                    application.applicationId!,
                    FormPage.personalInformation
                );
            } else return of(null);
        })
    );

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly applicationService: ApplicationService,
        private readonly snackBar: MatSnackBar,
        private readonly router: Router,
        private readonly _transloco: TranslocoService,
        private readonly _currentApplication: CurrentApplicationService,
        private readonly _client: ClientService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService,
        private readonly dynamicFormApi: DynamicFormApiService,
        private readonly dynamicFormOptionsService: DynamicFormOptionsService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _scrollTop: ScrollTopService
    ) {
        super();
        this.personalInformation = this._fb.group({});
    }

    ngAfterViewInit() {
        this.flyoutObserver = initFlyoutObserver(
            this.sectionWrappers,
            () => this._updateActiveFlyout(),
            this.destroyRef
        );
    }

    ngOnDestroy() {
        this.flyoutObserver?.disconnect();
    }

    private _updateActiveFlyout(): void {
        applyFlyoutUpdate(
            this.sectionWrappers,
            this._cdr,
            () => this._positionFlyoutTooltip(),
            (state) => {
                this.activeFlyoutIndex = state.activeFlyoutIndex;
                this.flyoutFlipUp = state.flyoutFlipUp;
            }
        );
    }

    private _positionFlyoutTooltip(): void {
        positionFlyoutTooltip(
            this.activeFlyoutIndex,
            this.flyoutFlipUp,
            this.sectionWrappers
        );
    }

    ngOnInit() {
        this._googleAnalyticsService.pageView(
            'Application: Personal Information'
        );
        this.formSections$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((sections) => {
                this.loadedSections = sections;
                this.personalInformation = buildDynamicForm(sections, this._fb);

                this.formValue$.pipe(first()).subscribe((formValue) => {
                    if (!formValue) {
                        return;
                    }

                    const values = buildFormValuesFromAnswers(
                        sections,
                        formValue
                    );
                    this.personalInformation.patchValue(values);
                    setTimeout(() => this._scrollTop.scrollToTop(), 0);
                });
            });

        // Read-only now.  Writing the selection moved to ApplicationEntryGuard, which
        // has already run by the time this page renders (LA-22939) — this only fetches the
        // providers to show in the flyout.
        this._currentApplication.latest
            .pipe(
                applicationPresent(),
                first(),
                switchMap((application: Application) => {
                    if (!application?.applicationId) return of(null);
                    this.applicationId = application.applicationId;
                    return this.applicationService.getSelectedProviderPrograms(
                        application.applicationId
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (res) => {
                    if (res) this.selectedProviders = res;
                },
                error: () => {
                    // The flyout is informational, so a failed fetch degrades to an empty list
                    // rather than interrupting a student who is part-way through the form.
                    this.selectedProviders = undefined;
                }
            });
    }

    getSectionValidators(
        section: ClientPageSectionModel
    ): PageSectionFieldValidator[] {
        return normalizeValidatorConfigs(section.validators, {
            forceUniqueTargetKeys: true
        });
    }

    isSectionVisible(section: ClientPageSectionModel): boolean {
        return isSectionVisible(section, this.personalInformation);
    }

    getSectionErrors(
        section: ClientPageSectionModel
    ): PageSectionFieldValidator[] {
        const anyFieldTouched = section.rows
            .reduce(
                (acc: PageSectionColumnModel[], row: PageSectionRowModel) =>
                    acc.concat(row.fields),
                []
            )
            .some(
                (field: PageSectionColumnModel) =>
                    this.personalInformation.get(field.key)?.touched
            );

        if (!this.submitted && !anyFieldTouched) {
            return [];
        }

        return this.getSectionValidators(section).filter(
            (validator) =>
                !validator.target &&
                this.personalInformation.hasError(
                    validator.errorKey ?? validator.name
                )
        );
    }

    isChanged(): boolean {
        return this.personalInformation?.touched ?? false;
    }

    isSaved(): boolean {
        return this.submitted;
    }

    onSubmitted() {
        if (this.submitting) return;
        this.personalInformation.markAllAsTouched();
        this.submitted = true;

        if (this.personalInformation.invalid) {
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, { duration: 4000 })
                );
            return;
        }

        if (!this.applicationId || this.loadedSections.length === 0) {
            return;
        }

        this.submitting = true;

        const payload = buildApplicationPageValueModel(
            this.loadedSections,
            this.personalInformation,
            this.applicationId,
            FormPage.personalInformation
        );

        this.applicationService
            .saveDynamicFormValues(
                this.applicationId,
                FormPage.personalInformation,
                payload
            )
            .pipe(
                switchMap(() =>
                    this._transloco
                        .selectTranslate('application.snack.saved')
                        .pipe(first())
                ),
                tap((message) => {
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    });
                    this.postSubmit();
                }),
                finalize(() => (this.submitting = false))
            )
            .subscribe();
    }

    private postSubmit() {
        this.submitted = true;
        this._currentApplication.load();
        this.router.navigateByUrl(
            `/${runtimeConfig.clientCode}/application/background`
        );
    }
}
